import { gql, useQuery } from '@apollo/client';
import { TableCard } from '@orn/shared-web/components/Table';
import { useNavigate } from 'react-router-dom';

import { PATHS } from '../../paths';

import type {
  AdminGetUsersWithTransactionReviewCountsQuery,
  AdminGetUsersWithTransactionReviewCountsQueryVariables,
} from './__generated__/UsersWithTransactionReviewsPane.gql';

const ADMIN_GET_USERS_WITH_TRANSACTION_REVIEW_COUNTS = gql`
  query AdminGetUsersWithTransactionReviewCounts {
    adminGetUsersWithTransactionReviewCounts {
      user {
        id
        name
        email
      }
      transactionReviewCount
    }
  }
`;

const UsersWithTransactionReviewsPane = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<
    AdminGetUsersWithTransactionReviewCountsQuery,
    AdminGetUsersWithTransactionReviewCountsQueryVariables
  >(ADMIN_GET_USERS_WITH_TRANSACTION_REVIEW_COUNTS, {
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-error-primary text-xl">Error loading users: {error.message}</div>
      </div>
    );
  }

  const users = data?.adminGetUsersWithTransactionReviewCounts ?? [];

  const handleUserClick = (userId: string) => {
    void navigate(PATHS.TRANSACTION_ANOMALY_FEEDBACK_FOR_USER(userId));
  };

  return (
    <div className="flex h-full w-full flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Users with transactions requiring review</h1>
        <p className="mt-2 text-sm text-gray-600">Click on a user to view their transactions that require review</p>
      </div>

      <TableCard.Root>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-primary border-tertiary border-b">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">User</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">
                  Transactions requiring review
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                    No users with transactions requiring review
                  </td>
                </tr>
              ) : (
                users.map((item) => (
                  <tr
                    key={item.user.id}
                    onClick={() => handleUserClick(item.user.id)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 font-medium">{item.user.name}</td>
                    <td className="px-6 py-4 text-gray-600">{item.user.email}</td>
                    <td className="px-6 py-4 text-right font-medium">{item.transactionReviewCount}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </TableCard.Root>
    </div>
  );
};

export default UsersWithTransactionReviewsPane;
